import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {useAppSelector} from '@/hooks';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {formatSecondsToTimeString} from '@Utils/date.util';
import {useTimelineHoverPosition, useIsTimelineItemDragging, useIsTimelineItemResizing, useTimelineWidthPerSecond} from '../../hooks/timeline-hooks';
import {useTimelineHoverLineRef, useTimelineScrollContainerRef} from '../../hooks/useTimelineRefs';
import {TIMELINE_HORIZONTAL_PADDING} from '../../timeline.types';
import styles from './timeline-hover-line.module.scss';

export function TimelineHoverLine(): ReactElement {
  const indicatorRef = useTimelineHoverLineRef();
  const scrollContainerRef = useTimelineScrollContainerRef();
  const timelineHoverPosition = useTimelineHoverPosition();
  const isItemDragging = useIsTimelineItemDragging();
  const isItemResizing = useIsTimelineItemResizing();
  const isSeekbarDragging = useAppSelector((state) => state.timeline.isSeekbarDragging);
  const timelineHeight = useAppSelector((state) => state.timeline.timelineHeight);
  const widthPerSecond = useTimelineWidthPerSecond();

  const lastValidPosition = useRef(0);
  if (timelineHoverPosition !== undefined) {
    lastValidPosition.current = timelineHoverPosition;
  }

  const isVisible = timelineHoverPosition !== undefined && !isItemDragging && !isItemResizing && !isSeekbarDragging;

  const hoverTime = lastValidPosition.current / widthPerSecond;
  const tooltipText = formatSecondsToTimeString(hoverTime, {showDecimals: true});

  const getTooltipLeft = (): number => {
    if (!scrollContainerRef?.current) {
      return 0;
    }
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    return containerRect.left + lastValidPosition.current - scrollLeft + TIMELINE_HORIZONTAL_PADDING;
  };

  const getTooltipTop = (): number => {
    if (!scrollContainerRef?.current) {
      return 0;
    }
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    return containerRect.top - 8;
  };

  return (
    <>
      <Tooltip
        style={{
          left: getTooltipLeft() + 1,
          top: getTooltipTop() + 2,
          position: 'fixed',
        }}
        className={`${styles.tooltip} ${isVisible ? '' : '_hidden'}`}
        id="timeline-hover-tooltip"
        text={<Text size={TextSize.XSMALL} val={tooltipText} />}
      />

      <div
        ref={indicatorRef}
        className={`${styles.hoverLine} ${isVisible ? styles.visible : ''}`}
        style={{transform: `translate(calc(${lastValidPosition.current}px - 50%), 0px)`}}
      >
        <div className={styles.line} style={{height: timelineHeight}} />
      </div>
    </>
  );
}
